import React, {ReactNode} from 'react';
import chroma from 'chroma-js';
import {isSafari} from "../lib/picos/browserFns";
import type {AxisTickProps} from "@nivo/axes";
import type {DatumId, PieSvgProps} from "@nivo/pie";
import type {SankeySvgProps} from "@nivo/sankey";

export default class ChartProps {

	static renderTick = (props: AxisTickProps<'linear'>) => {
		const {opacity, textAnchor, textBaseline, textX, textY, value, x, y} = props;
		const words = value.split(' ').length;
		const tY = words > 1 ? textY - 7 : textY + 5;

		return (
			<g
				transform={`translate(${x},${y})`}
				style={{opacity}}
			>
				{/* @ts-ignore */}
				<text alignmentBaseline={textBaseline}
					  textAnchor={textAnchor as "inherit" | "middle" | "start" | "end"}
					  transform={`translate(${textX},${tY})`}
				>
					{this.getTspanGroups(value, 12)}
				</text>
			</g>
		);
	};

	/**
	 * Create a <type> group with lines (line wrapping for svg).
	 *
	 * @param {'tspan'|'div'} type
	 * @param {string} value
	 * @param {number} maxLineLength
	 * @param {number} maxLines
	 * @param {string} [className]
	 * @param {number} [xOffset] - if present, start x pixels to the left
	 * @returns {ReactNode[]}
	 */
	static _getGroups = (type: 'tspan' | 'div', value: string, maxLineLength: number, maxLines: number, className?: string, xOffset?: number): ReactNode[] => {
		const words = (typeof (value) === 'string') ? value.split(' ') : [value];

		//reduces the words into lines of maxLineLength
		const assembleLines = words.reduce((acc, word) => {
			//if the current line isn't empty and the word + current line is larger than the allowed line size,
			// create a new line and update the current line
			if ((word + ' ' + acc.currLine).length >= maxLineLength && acc.currLine !== '') {
				return {
					lines: acc.lines.concat([acc.currLine]),
					currLine: word
				};
			}
			//otherwise add the word to the current line
			return Object.assign(Object.assign({}, acc), {currLine: (acc.currLine + ' ' + word).trim()});
		}, {lines: [], currLine: ''});

		//add the ending state of current line (the last line) to lines
		const allLines = assembleLines.lines.concat([assembleLines.currLine]);

		//for now, only take first 2 lines due to tick spacing and possible overflow
		const lines = allLines.slice(0, maxLines);
		let result = [];
		let dy = 0;

		const x = xOffset ? -1 * xOffset : 0;

		lines.forEach((lineText, i) => {
			// Add this element to the result
			if (type === 'tspan') {
				result.push(<tspan className={className} x={x} dy={dy} key={i}>
					{(i === maxLines - 1 && allLines.length > maxLines) ? lineText.slice(0, maxLineLength - 3) + '...' : lineText}
				</tspan>);
			}
			else {
				result.push(<div key={i} className={className}>
					{(i === maxLines - 1 && allLines.length > maxLines) ? lineText.slice(0, maxLineLength - 3) + '...' : lineText}
				</div>);
			}

			//increment dy to render next line text below
			if (dy === 0) dy += 20;
		});

		return result;
	}

	/**
	 * Create a <tspan> group with lines (line wrapping for svg).
	 *
	 * @param {string} value
	 * @param {number} maxLineLength
	 * @param {number} maxLines
	 * @param {string} [className] - optional classname to attach
	 * @param {number} [xOffset] - if present, move text x pixels to the left
	 */
	static getTspanGroups = (value: string | DatumId, maxLineLength: number, maxLines: number = 3, className?: string, xOffset?: number): Array<ReactNode> => {
		return ChartProps._getGroups('tspan', value as string, maxLineLength, maxLines, className, xOffset);
	};

	/**
	 * Create a <div> group with lines (line wrapping for html).
	 *
	 * @param {string} value
	 * @param {number} maxLineLength
	 * @param {number} maxLines
	 * @returns {ReactNode[]}
	 */
	static getDivGroups = (value: string, maxLineLength: number, maxLines: number = 3): ReactNode[] => {
		return ChartProps._getGroups('div', value, maxLineLength, maxLines);
	};

	static fullPieProps: Partial<PieSvgProps<any>> = {
		theme: {
			labels: {
				text: {
					color: '#000',
					fontSize: 16,
				},
			},
		},

		margin: {top: 50, right: 120, bottom: 50, left: 120},
		innerRadius: 0.5,
		padAngle: 3,
		cornerRadius: 3,
		borderWidth: 1,
		borderColor: {from: 'color', modifiers: [['darker', 0.2]]},
		arcLabelsSkipAngle: 10,
		arcLabelsRadiusOffset: 6,
		arcLabelsTextColor: "#333333",
		arcLinkLabelsOffset: 0,
		arcLinkLabelsDiagonalLength: 16,
		arcLinkLabelsStraightLength: 24,
		arcLinkLabelsThickness: 2,
		arcLinkLabelsColor: "black", //{from: 'color'},
		animate: true,
		legends: [
			{
				anchor: 'bottom',
				direction: 'row',
				translateY: 56,
				itemWidth: 100,
				itemHeight: 18,
				itemTextColor: '#999',
				symbolSize: 18,
				symbolShape: 'circle',
				effects: [
					{
						on: 'hover',
						style: {
							itemTextColor: '#000'
						}
					}
				]
			}
		]
	}

	static baseFlowProps: Partial<SankeySvgProps<any, any>> = {
		margin: {top: 10, right: 150, bottom: 25, left: 110},
		align: "justify",
		nodeOpacity: 1,
		nodeThickness: 20,
		nodeInnerPadding: 3,
		nodeSpacing: 20,
		nodeBorderWidth: 0,
		nodeBorderColor: {from: 'color', modifiers: [['darker', 0.8]]},
		linkBlendMode: 'normal',
		linkOpacity: 0.5,
		linkHoverOthersOpacity: 0.1,
		enableLinkGradient: !isSafari(),
		enableLabels: true,
		layout: "horizontal",
		labelPosition: "outside",
		labelOrientation: "horizontal",
		labelPadding: 16,
		labelTextColor: {from: 'color', modifiers: [['darker', 2]]},
		sort: 'descending',
		theme: {
			text: {
				fontSize: 16
			}
		},
	}

	static leadFlowProps = {
		...ChartProps.baseFlowProps
	};

	static bulletProps = {
		theme: {
			textColor: '#000',
			fontSize: 14
		},
		margin: {top: 20, right: 20, bottom: 50, left: 120},
		width: 600,
		spacing: 40,
		titleAlign: "start",
		titleOffsetX: -70,
		measureSize: .8,
		markerSize: 1.2,
		animate: true,
		motionStiffness: 90,
		motionDamping: 12,
		measureColors: ['rgb(175, 221, 166)', 'rgb(221, 150, 150)'],
		rangeColors: ['#ddd'],
		markerColors: ['black']
	}

	static funnelColors = [
		'#146CF680', '#188AF080', '#00B7D880', '#00D4B080', '#00E54B80', '#00F80080', '#FFFF0080'
	]

	static MINT = '#98ff98';
	static RED = '#ffadad';
	static ORANGE = '#ffc04d';
	static YELLOW = '#fdffb6';
	static GREEN = '#caffbf';
	static CYAN = '#9bf6ff';
	static BLUE = '#a0c4ff';
	static PURPLE = '#bdb2ff';
	static VIOLET = '#ffc6ff';
	static WHITE = '#fffffc';
	static GRAY = '#cccccc'

	static BASE_COLORS = ['#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6', '#bcf60c',
		'#fabebe', '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1',
		'#000075', '#808080', '#000000'];

	static Colors = {
		RedGreen: [ChartProps.RED, ChartProps.GREEN],
		GreenRed: [ChartProps.GREEN, ChartProps.RED],
		GreenRedGray: [ChartProps.GREEN, ChartProps.RED, ChartProps.GRAY],
		GreenOrangeRed: [ChartProps.GREEN, ChartProps.ORANGE, ChartProps.RED],
		Rainbow: [ChartProps.RED, ChartProps.ORANGE, ChartProps.YELLOW, ChartProps.GREEN, ChartProps.CYAN,
			ChartProps.BLUE, ChartProps.PURPLE, ChartProps.VIOLET, ChartProps.WHITE, ChartProps.GRAY],
		RainbowNoWhites: [ChartProps.RED, ChartProps.ORANGE, ChartProps.YELLOW, ChartProps.GREEN, ChartProps.CYAN,
			ChartProps.BLUE, ChartProps.PURPLE, ChartProps.VIOLET, ChartProps.GRAY, ChartProps.MINT],
		RainbowDarkerNoWhites: [ChartProps.RED, ChartProps.ORANGE, ChartProps.YELLOW, ChartProps.GREEN, ChartProps.CYAN,
			ChartProps.BLUE, ChartProps.PURPLE, ChartProps.VIOLET, ChartProps.GRAY, ChartProps.MINT]
			.map(color => chroma(color).darken(.75).hex()),
		Distinct: ChartProps.BASE_COLORS.map(c => chroma(c).darken(.8)),
		DistinctLight: ChartProps.BASE_COLORS.map(c => chroma(c).darken(.8).alpha(.5)),
	}

	static generateColors(darken: number, alpha: number) {
		return ChartProps.BASE_COLORS.map(c => chroma(c).darken(darken).alpha(alpha).hex());
	}
}
